/**
 * Transform CategoryTree[] into GroupedSelect format.
 *
 * Converts a hierarchical category tree into the flat group/item structure
 * expected by the GroupedSelect component.
 */

import { getCategoryIconKey } from '@/features/categories/components/icon-picker';
import { IconKey, type GroupedSelectItem, type SelectGroup } from '@/shared/components';
import type { CategoryTree, SplitType } from '@/shared/types';

function flattenChildrenToItems(
  children: CategoryTree[],
  matchesFilter: (cat: CategoryTree) => boolean,
  depth: number,
): GroupedSelectItem[] {
  const items: GroupedSelectItem[] = [];
  for (const child of children) {
    if (matchesFilter(child)) {
      items.push({
        id: child.id,
        label: child.name,
        icon: getCategoryIconKey(child.icon),
        color: child.color ?? undefined,
        depth,
        hasChildren: child.children.length > 0,
      });
    }
    if (child.children.length > 0) {
      items.push(...flattenChildrenToItems(child.children, matchesFilter, depth + 1));
    }
  }
  return items;
}

export interface CategoriesToGroupsOptions {
  filterBySplitType?: SplitType;
  alwaysIncludeId?: string | null;
}

export function categoriesToGroups(
  categoryTree: CategoryTree[],
  options?: CategoriesToGroupsOptions
): {
  groups: SelectGroup[];
  topLevelItems: GroupedSelectItem[];
} {
  const { filterBySplitType, alwaysIncludeId } = options ?? {};

  const matchesFilter = (cat: CategoryTree) => {
    if (!filterBySplitType || filterBySplitType === 'custom') return true;
    if (alwaysIncludeId && cat.id === alwaysIncludeId) return true;
    return cat.default_split_type === filterBySplitType;
  };

  const topLevelItems: GroupedSelectItem[] = [
    {
      id: '__uncategorized__',
      label: 'Uncategorized',
      icon: IconKey.Tag
    }
  ];

  const groups: SelectGroup[] = [];

  for (const parent of categoryTree.filter((c) => !c.is_system)) {
    const parentIconKey = getCategoryIconKey(parent.icon);
    const items: GroupedSelectItem[] = [];

    if (parent.children.length > 0) {
      // Add parent as "(General)" if it passes the filter
      if (matchesFilter(parent)) {
        items.push({
          id: parent.id,
          label: parent.name,
          icon: parentIconKey,
          color: parent.color ?? undefined
        });
      }

      // Recursively add children (and grandchildren, etc.) that pass the filter
      items.push(...flattenChildrenToItems(parent.children, matchesFilter, 0));
    } else if (matchesFilter(parent)) {
      items.push({
        id: parent.id,
        label: parent.name,
        icon: parentIconKey,
        color: parent.color ?? undefined
      });
    }

    // Only include group if it has visible items
    if (items.length > 0) {
      groups.push({
        id: parent.id,
        label: parent.name,
        icon: parentIconKey,
        color: parent.color ?? undefined,
        items
      });
    }
  }

  return { groups, topLevelItems };
}
